import { CookieStorage } from '@24i/nxg-sdk-quantum';
import { consentDisabled, consentNotReady } from '../consent';
import {
    consentCategories,
    ConsentWatcher,
    IOneTrustWeb,
    OneTrustConfigWeb,
    OneTrustConsent,
} from '../types';

export class OneTrustWeb implements IOneTrustWeb {
    private disabled = false;

    async init(config?: OneTrustConfigWeb) {
        if (!config?.enabled || !config.scriptUrl || !config.domainScript) {
            this.disabled = true;
            return;
        }

        await this.loadOneTrustScript(config.scriptUrl, config.domainScript);
        this.dispatchConsentChange();
    }

    /* eslint-disable class-methods-use-this */
    showPreferenceModal() {
        if (typeof window?.OneTrust?.ToggleInfoDisplay === 'function')
            window.OneTrust.ToggleInfoDisplay();
    }

    /* eslint-disable class-methods-use-this */
    loadOneTrustScript = (url: string, domainScript: string): Promise<void> =>
        new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = url;
            script.setAttribute('data-domain-script', domainScript);
            script.onload = () => resolve();
            script.onerror = (event) => {
                let error: Error | undefined;
                if (typeof event === 'string') {
                    error = new Error(event);
                } else if (event instanceof ErrorEvent) {
                    if (event.error instanceof Error) error = event.error;
                    else if (event.message) error = new Error(event.message);
                }
                if (!error) error = new Error('Failed to load OneTrust');
                reject(error);
            };
            document.head.appendChild(script);

            // OptanonWrapper function reserved by SDK and used after initialize
            const callback = document.createElement('script');
            callback.type = 'text/javascript';
            callback.text = `function OptanonWrapper() {
        const otConsentSdk = document.getElementById("ot-sdk-btn-floating");
        if (otConsentSdk) {
            otConsentSdk.style.display = "none";
        }
    }`;
            document.body.appendChild(callback);
        });

    getConsentStatus = async (): Promise<OneTrustConsent> => {
        if (!window?.OneTrust) {
            return this.disabled ? consentDisabled : consentNotReady;
        }
        const activeCategories = `,${window?.OnetrustActiveGroups},`;
        const personalAds = consentCategories.some((categoryId) =>
            activeCategories.includes(categoryId)
        );
        let tcfConsentString: string | null = null;
        let gppConsentString = '';
        let gppSectionId = '';
        const usLocation =
            typeof window.OneTrust?.getGeolocationData === 'function'
                ? window.OneTrust?.getGeolocationData()?.country === 'US'
                : false;

        const gppString = await CookieStorage.getItem('OTGPPConsent');
        gppConsentString = usLocation ? gppString || '' : '';

        window.OneTrust?.getVendorConsentsRequestV2?.((e) => {
            tcfConsentString = e?.tcString || null;
            gppSectionId = usLocation ? e?.gppSid || '' : '';
        });

        return {
            doNotTrack: !personalAds,
            limitAdvertisementTracking: !personalAds,
            tcfConsentString,
            gppConsentString,
            gppSectionId,
        };
    };

    private readonly watchers: ConsentWatcher[] = [];

    private addedConsentListener = false;

    dispatchConsentChange(): void {
        this.watchers.forEach((watcher) => watcher());
    }

    watchConsentStatus(callback: ConsentWatcher): () => void {
        if (!this.addedConsentListener) {
            this.addedConsentListener = true;
            window?.OneTrust?.OnConsentChanged(() => this.dispatchConsentChange());
        }
        this.watchers.push(callback);
        return () => this.unwatchConsentStatus(callback);
    }

    unwatchConsentStatus(callback: ConsentWatcher): void {
        const watcherIndex = this.watchers.indexOf(callback);
        if (watcherIndex < 0) return;
        this.watchers.splice(watcherIndex, 1);
    }
}
